import { MediaImage } from "@/components/ui/MediaImage";
import { VideoBlock } from "@/components/ui/VideoBlock";
import { useMediaByKey } from "@/content-context";
import type { BlockDoc } from "@/content";

/**
 * Rich text, rendered from structured blocks.
 *
 * There is no HTML field anywhere in this CMS and this component is why it does
 * not need one. An editor chooses a block kind and types text; the component
 * decides the element. A paste of `<script>` lands in a paragraph as visible
 * characters, because React escapes text children and nothing here calls
 * dangerouslySetInnerHTML.
 *
 * That constraint costs almost nothing editorially. Headings, paragraphs,
 * lists, sourced quotes, images, video, callouts and fact lists cover what
 * anybody has actually needed to write on this site, and each one gets the
 * site's own typography rather than whatever a pasted fragment carried with it.
 */
export function Blocks({ blocks, className = "" }: { blocks?: BlockDoc[]; className?: string }) {
  const byKey = useMediaByKey();
  if (!blocks?.length) return null;

  return (
    <div className={`prose-body ${className}`}>
      {blocks.map((block, index) => {
        const key = `${block.kind}-${index}`;

        switch (block.kind) {
          case "heading": {
            const level = Math.min(Math.max(block.level ?? 2, 2), 4);
            const Tag = (`h${level}` as unknown) as "h2";
            return (
              <Tag
                key={key}
                className="mt-10 first:mt-0"
                style={{ fontSize: level === 2 ? "var(--step-h3)" : "var(--step-h4)" }}
              >
                {block.text}
              </Tag>
            );
          }

          case "paragraph":
            return (
              <p key={key} className="mt-4 text-muted">
                {block.text}
              </p>
            );

          case "list":
            return (
              <ul key={key} className="mt-4 space-y-3 text-muted">
                {(block.items ?? []).map((line) => (
                  <li key={line} className="flex gap-3.5">
                    <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue" />
                    {line}
                  </li>
                ))}
              </ul>
            );

          /*
           * A quote without an attribution is not rendered as a quote.
           *
           * A pull quote in an organisation's own voice, with nobody's name on
           * it, reads as a testimonial the organisation wrote about itself.
           * Where there is no source it falls through to a plain paragraph.
           */
          case "quote":
            return block.attribution ? (
              <figure key={key} className="mt-8 border-l-2 border-sky pl-6">
                <blockquote className="font-serif text-[1.25rem] leading-snug text-midnight">
                  {block.text}
                </blockquote>
                <figcaption className="mt-3 text-[0.875rem] text-muted">{block.attribution}</figcaption>
              </figure>
            ) : (
              <p key={key} className="mt-4 text-muted">
                {block.text}
              </p>
            );

          case "image": {
            const item = byKey(block.mediaKey);
            if (!item) return null;
            return (
              <figure key={key} className="mt-8">
                <MediaImage
                  item={item}
                  sizes="(min-width: 1024px) 720px, 100vw"
                  className="w-full rounded-[16px]"
                  style={{ height: "auto" }}
                />
                {block.text || item.caption ? (
                  <figcaption className="mt-3 text-[0.875rem] text-muted">
                    {block.text || item.caption}
                  </figcaption>
                ) : null}
              </figure>
            );
          }

          case "video":
            return (
              <div key={key} className="mt-8">
                <VideoBlock
                  url={block.videoUrl}
                  poster={byKey(block.mediaKey)}
                  title={block.text || "Video"}
                  radius={16}
                />
              </div>
            );

          case "callout":
            return (
              <div
                key={key}
                role="note"
                className={`notice mt-8 ${block.tone === "warning" ? "notice-rose" : "notice-amber"}`}
              >
                <p className="text-[0.9375rem] text-ink">{block.text}</p>
              </div>
            );

          case "factList":
            return (
              <dl key={key} className="mt-8 space-y-3.5">
                {(block.facts ?? []).map((fact) => (
                  <div
                    key={fact.label}
                    className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-1 border-b border-line pb-3.5 last:border-0"
                  >
                    <dt className="text-[0.9375rem] text-muted">{fact.label}</dt>
                    <dd className="font-serif text-[1.0625rem] text-midnight">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}
